import { memo, useState, useMemo, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getTooltipStyle, LABEL_STYLE } from '../shared/constants';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { useInnovationData, PREDEFINED_DISCONTINUATION_REASONS } from './useInnovationData';
import { useResponsiveYAxisWidth } from '../shared/useResponsiveYAxisWidth';
import { Eye, X, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const TYPE_COLORS = {
  'Innovation development': '#8b5cf6',
  'Innovation use': '#22c55e',
  'Innovation Package': '#ec4899',
};

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

const PREDEFINED_FULL_TEXTS = Object.keys(PREDEFINED_DISCONTINUATION_REASONS);

function DiscontinuedDetailModal({ results, byReason, onClose }) {
  const [search, setSearch] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const handleKeyDown = useCallback((e) => { if (e.key === 'Escape') onClose(); }, [onClose]);
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handleKeyDown); document.body.style.overflow = ''; };
  }, [handleKeyDown]);

  const reasonOptions = useMemo(() => {
    if (!byReason) return [];
    return Object.entries(byReason)
      .sort((a, b) => b[1].count - a[1].count)
      .map(([shortLabel]) => shortLabel);
  }, [byReason]);

  const filtered = useMemo(() => {
    const sorted = [...results].sort((a, b) => String(a.resultCode || '').localeCompare(String(b.resultCode || '')));
    let out = sorted;

    // Apply reason filter
    if (reasonFilter) {
      if (reasonFilter === 'Other') {
        out = out.filter(r => {
          const raw = r.discontinuedReason || '';
          if (!raw) return true; // no reason = Other
          return !PREDEFINED_FULL_TEXTS.some(ft => raw.includes(ft));
        });
      } else {
        const fullText = byReason?.[reasonFilter]?.fullText;
        if (fullText) {
          out = out.filter(r => (r.discontinuedReason || '').includes(fullText));
        }
      }
    }

    // Apply text search
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter(r =>
        String(r.resultCode || '').toLowerCase().includes(q) ||
        (r.title || '').toLowerCase().includes(q) ||
        (r.discontinuedReason || '').toLowerCase().includes(q)
      );
    }

    return out;
  }, [results, search, reasonFilter, byReason]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows = filtered.slice((page - 1) * perPage, page * perPage);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, perPage, reasonFilter]);

  const isFiltered = search.trim() || reasonFilter;
  const countLabel = isFiltered
    ? `${filtered.length} of ${results.length}`
    : `${results.length}`;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 flex flex-col w-full max-w-5xl max-h-[80vh] bg-[var(--surface-primary)] rounded-xl shadow-2xl overflow-hidden mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--border-primary)]">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">Discontinued Innovations ({countLabel})</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--surface-hover)] text-[var(--text-tertiary)]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search + Reason filter */}
        <div className="px-5 py-3 border-b border-[var(--border-primary)] flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by result code, title, or reason..."
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-[var(--border-primary)] bg-[var(--surface-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
          </div>
          {reasonOptions.length > 0 && (
            <select
              value={reasonFilter}
              onChange={(e) => setReasonFilter(e.target.value)}
              className="px-3 py-2 text-sm rounded-lg border border-[var(--border-primary)] bg-[var(--surface-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500/30 min-w-[180px]"
            >
              <option value="">All reasons</option>
              {reasonOptions.map(label => (
                <option key={label} value={label}>{label}</option>
              ))}
            </select>
          )}
        </div>

        {/* Table */}
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col className="w-[10%]" />
              <col className="w-[35%]" />
              <col className="w-[12%]" />
              <col className="w-[43%]" />
            </colgroup>
            <thead className="sticky top-0 bg-[var(--surface-secondary)] text-left">
              <tr>
                <th className="px-4 py-2 font-medium text-[var(--text-secondary)]">Result Code</th>
                <th className="px-4 py-2 font-medium text-[var(--text-secondary)]">Title</th>
                <th className="px-4 py-2 font-medium text-[var(--text-secondary)]">Type</th>
                <th className="px-4 py-2 font-medium text-[var(--text-secondary)]">Reason for Discontinuation</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-primary)]">
              {pageRows.map((r) => (
                <tr key={r.resultCode || r.id}>
                  <td className="px-4 py-2 text-[var(--text-primary)] font-mono text-xs">{r.resultCode || '—'}</td>
                  <td className="px-4 py-2 text-[var(--text-primary)] break-words">{r.title || '—'}</td>
                  <td className="px-4 py-2 text-[var(--text-secondary)] text-xs">{r.type?.replace('Innovation ', '') || '—'}</td>
                  <td className="px-4 py-2 text-[var(--text-secondary)] text-xs break-words">{r.discontinuedReason || '—'}</td>
                </tr>
              ))}
              {pageRows.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-[var(--text-tertiary)]">No results match your search.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-[var(--border-primary)] text-xs text-[var(--text-secondary)]">
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              className="px-2 py-1 rounded border border-[var(--border-primary)] bg-[var(--surface-primary)] text-[var(--text-primary)] text-xs focus:outline-none"
            >
              {PER_PAGE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span>Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => p - 1)}
              disabled={page <= 1}
              className="p-1 rounded hover:bg-[var(--surface-hover)] disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= totalPages}
              className="p-1 rounded hover:bg-[var(--surface-hover)] disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export const DiscontinuedInnovationsChart = memo(function DiscontinuedInnovationsChart() {
  const { discontinuedData, innovationResults } = useInnovationData();
  const [showModal, setShowModal] = useState(false);
  const reasonYAxisWidth = useResponsiveYAxisWidth(200);
  if (!discontinuedData) return null;

  const { total, totalInnovations, byType, byReadinessLevel, byReason, rate, results } = discontinuedData;

  // Even if total=0, show the callout saying "none discontinued"
  const typeData = Object.entries(byType)
    .filter(([, count]) => count > 0)
    .map(([type, count]) => ({ name: type, value: count }));

  const levels = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const readinessData = levels
    .map(l => ({ level: `Level ${l}`, count: byReadinessLevel[l] || 0 }));

  const reasonData = Object.entries(byReason || {})
    .map(([shortLabel, { count, fullText }]) => ({ name: shortLabel, count, fullText }))
    .sort((a, b) => b.count - a.count);

  const exportData = [
    { Metric: 'Total discontinued innovations', Value: total },
    { Metric: 'Discontinuation rate (%)', Value: rate },
    ...Object.entries(byType).map(([type, count]) => ({ Metric: `Discontinued ${type}`, Value: count })),
    ...levels.filter(l => byReadinessLevel[l]).map(l => ({ Metric: `Readiness Level ${l} at discontinuation`, Value: byReadinessLevel[l] })),
    ...reasonData.map(d => ({ Metric: `Reason: ${d.name}`, Value: d.count })),
  ];

  return (
    <ChartWrapper title="Discontinued Innovations" info="Innovations marked as discontinued: count, rate, and their readiness levels and types at the time of discontinuation." chartData={exportData} exportFilename="discontinued-innovations" sourceResults={innovationResults}>
      <div className="mb-4 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
        This view shows all discontinued innovations for the selected program, independent of the Status filter.
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="bg-red-50 rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-red-700">{total}</p>
          <p className="text-sm text-red-600">Discontinued</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-gray-700">{totalInnovations}</p>
          <p className="text-sm text-gray-600">Total innovations</p>
        </div>
        <div className="bg-orange-50 rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-orange-700">{rate}%</p>
          <p className="text-sm text-orange-600">Discontinuation rate</p>
        </div>
      </div>

      {total > 0 && (
        <>
          <div className="flex justify-end mb-2">
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Eye className="w-3.5 h-3.5" />
              View details
            </button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {typeData.length > 0 && (
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-2">By Innovation Type</h5>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart margin={{ left: 40, right: 40 }}>
                    <Pie data={typeData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} innerRadius={35} paddingAngle={2}
                      animationBegin={0} animationDuration={400} label={({ name, value }) => { const s = name.replace('Innovation ', ''); return `${s.charAt(0).toUpperCase()}${s.slice(1)} (${value})`; }}>
                      {typeData.map((entry) => (
                        <Cell key={entry.name} fill={TYPE_COLORS[entry.name] || '#6b7280'} />
                      ))}
                    </Pie>
                    <Tooltip content={({ active, payload }) => {
                      if (!active || !payload?.[0]) return null;
                      const d = payload[0].payload;
                      return (
                        <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.4 }}>
                          <div style={{ fontWeight: 600, color: TYPE_COLORS[d.name] || '#1e293b' }}>{d.name}</div>
                          <div style={{ color: '#4b5563' }}>{d.value} innovation{d.value !== 1 ? 's' : ''}</div>
                        </div>
                      );
                    }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            {readinessData.length > 0 && (
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-2">Readiness Level at Discontinuation</h5>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={readinessData} margin={{ top: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="level" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip content={({ active, payload }) => {
                      if (!active || !payload?.[0]) return null;
                      const d = payload[0].payload;
                      return (
                        <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.4 }}>
                          <div style={{ fontWeight: 600, color: '#1e293b' }}>{d.level}</div>
                          <div style={{ color: '#4b5563' }}>{d.count} innovation{d.count !== 1 ? 's' : ''}</div>
                        </div>
                      );
                    }} />
                    <Bar dataKey="count" fill="#ef4444" radius={[4, 4, 0, 0]} animationBegin={0} animationDuration={400}>
                      <LabelList dataKey="count" position="top" style={LABEL_STYLE} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          {reasonData.length > 0 && (
            <div className="mt-6">
              <h5 className="text-sm font-medium text-gray-700 mb-2">Reason for Discontinuation</h5>
              <ResponsiveContainer width="100%" height={Math.max(200, reasonData.length * 40)}>
                <BarChart data={reasonData} layout="vertical" margin={{ left: 10, right: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: reasonYAxisWidth < 200 ? 9 : 11 }} width={reasonYAxisWidth} />
                  <Tooltip content={({ active, payload }) => {
                    if (!active || !payload?.[0]) return null;
                    const d = payload[0].payload;
                    return (
                      <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.4, maxWidth: '360px' }}>
                        <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>{d.name}</div>
                        <div style={{ color: '#4b5563', marginBottom: 4 }}>{d.count} innovation{d.count !== 1 ? 's' : ''}</div>
                        {d.fullText !== d.name && (
                          <div style={{ color: '#374151', fontSize: '11px' }}>{d.fullText}</div>
                        )}
                      </div>
                    );
                  }} />
                  <Bar dataKey="count" fill="#f97316" radius={[0, 4, 4, 0]} animationBegin={0} animationDuration={400}>
                    <LabelList dataKey="count" position="right" style={LABEL_STYLE} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p className="mt-1 text-xs text-gray-400">
                Some innovations cite multiple reasons. Total reasons cited: {reasonData.reduce((s, d) => s + d.count, 0)}.
              </p>
            </div>
          )}
        </>
      )}
      {total === 0 && (
        <div className="text-center py-6 text-gray-500">
          <p className="text-lg">No discontinued innovations in the current selection.</p>
        </div>
      )}

      {showModal && <DiscontinuedDetailModal results={results} byReason={byReason} onClose={() => setShowModal(false)} />}
    </ChartWrapper>
  );
});
